import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Divider, Checkbox, Result } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import useStore from '../../store/useStore';
import { authApi } from '../../services/authApi';

const { Title, Text } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setUser } = useStore();

  const handleFinish = async (values) => {
    setError(null);
    setLoading(true);
    try {
      const user = await authApi.login(values.email, values.password);
      setUser(user);
      const params = new URLSearchParams(window.location.search);
      const next = params.get('next') || '/dashboard';
      navigate(next, { replace: true });
    } catch (e) {
      // Handle specific error messages from backend
      let errorMessage = 'Sign in failed';
      
      if (e?.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="w-full max-w-4xl">
        <Card className="shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Brand / Marketing panel */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-sky-500 to-indigo-600 p-6 text-white">
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center font-bold">IF</div>
                  <span className="text-lg font-semibold tracking-wide">QQINV</span>
                </div>
                <Title level={3} className="!text-white !mb-2">Welcome back</Title>
                <Text className="!text-white/80">Sign in to manage stock, orders, and more with a modern inventory experience.</Text>
              </div>
              <ul className="mt-8 space-y-2 text-sm text-white/90">
                <li>• Real-time stock insights</li>
                <li>• Barcode and E-Way bill support</li>
                <li>• Powerful search and automation</li>
              </ul>
            </div>

            {/* Form panel */}
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <Title level={3} className="!mb-1 dark:!text-slate-100">Sign in</Title>
                <Text className="dark:!text-slate-400">Use your email and password to continue</Text>
              </div>

              {error && (
                <div className="mb-4">
                  <Result
                    status="error"
                    title="Authentication failed"
                    subTitle={error}
                  />
                </div>
              )}

              <Form layout="vertical" form={form} onFinish={handleFinish}>
                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please enter your email' }, { type: 'email' }] }>
                  <Input size="large" prefix={<MailOutlined />} placeholder="you@example.com" autoComplete="email" />
                </Form.Item>
                <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please enter your password' }] }>
                  <Input.Password size="large" prefix={<LockOutlined />} placeholder="••••••••" autoComplete="current-password" />
                </Form.Item>
                <div className="flex items-center justify-between mb-3">
                  <Checkbox>Remember me</Checkbox>
                  <a href="#" className="text-sm">Forgot password?</a>
                </div>
                <Button type="primary" htmlType="submit" block size="large" loading={loading} disabled={loading}>Sign in</Button>
              </Form>

              <Divider plain className="!my-6">or</Divider>
              <div className="grid grid-cols-1 gap-2">
                <Button block>Continue with Google</Button>
              </div>

              <div className="mt-6 text-center space-y-1">
                <div>
                  <Text className="dark:!text-slate-400">No account? <Link to="/register">Register</Link></Text>
                </div>
                <div>
                  <Text type="secondary" className="text-xs">Superadmin? <Link to="/superadmin/login">Login (temporary)</Link></Text>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
